import { _event } from "./_event";
import { _inview } from "./_inview";
import { _splitText } from "./_splitText";
import SplitType from "split-type";

type AnimateProps = {
    debug?: boolean;
    delay?: number;
    adjacentDelay?: number;
    staggerDelay: number;
    duration?: number;
    keyframe?: string;
};
export class _animate {
    debug?: boolean;
    delay?: number;
    adjacentDelay?: number;
    staggerDelay?: number;
    duration?: number;
    keyframe?: string;

    constructor(props?: AnimateProps) {
        if ((window as any)._animateInstance) return;
        (window as any)._animateInstance = this;

        this.debug = props?.debug ?? false;
        this.delay = props?.delay ?? 0.1;
        this.adjacentDelay = props?.adjacentDelay ?? this.delay;
        this.staggerDelay = this.adjacentDelay;
        this.duration = props?.duration ?? 0.8;
        this.keyframe = props?.keyframe ?? "fadeInUpSmall";

        this.init();
    }

    init() {
        this.style();

        // find all the animate containers
        document.querySelectorAll("[data-animate]").forEach((item) => {
            const _item = item as HTMLElement;
            const _dataset = _item.dataset.animate.split(",");

            const _relation = _dataset[0] ? _dataset[0] : "";
            let _delay = _dataset[1]
                ? !isNaN(parseFloat(_dataset[1]))
                    ? parseFloat(_dataset[1])
                    : _dataset[1]
                : this.delay; // default animation delay
            let _duration = _dataset[2] && !isNaN(parseFloat(_dataset[2])) ? parseFloat(_dataset[2]) : this.duration; // default animation duration

            if (_relation === "parent") {
                // for parent

                // find all the children of this parent
                _item.querySelectorAll('[data-animate="children"]').forEach((item) => {
                    const _item = item;

                    // find again the sub-children
                    Array.from(_item.children).forEach((item) => {
                        const _item = item;

                        // if this children has children too
                        if (_item.dataset.animate === "children") {
                            Array.from(_item.children).forEach((item) => {
                                const _item = item;

                                _item.dataset.animate = "child"; // mark as child

                                this.set(_item, _delay, _duration);

                                _delay += this.adjacentDelay; // stagger the delay for all child
                            });
                        } else {
                            if (_item.dataset.animate) return;

                            _item.dataset.animate = "child"; // mark as child

                            this.set(_item, _delay, _duration);

                            _delay += this.adjacentDelay; // stagger the delay for all child
                        }
                    });
                });

                _item.querySelectorAll('[data-animate="children-inview"]').forEach((item) => {
                    Array.from(item.children).forEach((item) => {
                        item.dataset.animate = "";
                        // item.dataset.animateDelay = "stagger";
                        item.classList.add("opacity-0");
                    });
                });
            } else {
                // for single item
                if (!["children", "child"].includes(_relation)) this.set(_item, _delay, _duration);
            }
        });

        // animate the containers upon inview
        document.querySelectorAll("[data-animate]").forEach((item) => {
            const _item = item as HTMLElement;
            const _dataset = _item.dataset.animate.split(",");
            const _relation = _dataset[0] ? _dataset[0] : "";

            new _inview({
                target: _item,
                callback: () => {
                    if (_relation === "parent") {
                        // for parent
                        _item.classList.remove("opacity-0");
                        _item.removeAttribute("data-animate");

                        // animate each child
                        _item.querySelectorAll('[data-animate="child"]').forEach((item) => {
                            const _item = item as HTMLElement;

                            this.show(_item, (target) => {
                                this.clean(target);
                            });

                            _event.dispatch(_item, "visible");
                        });
                    } else if (_relation === "children") {
                        // this is the parent of child, no animation, just show the content
                        _item.classList.remove("opacity-0");
                        _item.removeAttribute("data-animate");
                    } else if (_relation === "child") {
                        // do nothing, as their parent will trigger visibility for each of them
                        return;
                    } else {
                        // for single item
                        this.show(_item, (target) => {
                            this.clean(target);
                        });
                    }
                },
            });
        });

        // for header titles or splittext animate
        if (typeof _splitText !== "undefined" && typeof SplitType !== "undefined") {
            document.querySelectorAll(".js-splittext").forEach((item) => {
                const _item = item as HTMLElement;
                const _splittext = new _splitText({ target: _item });

                if (!_item.dataset.animate) return;
                _item.classList.add("js-splittext-animate");

                _item.addEventListener("visible", () => {
                    _item.classList.add("js-splittext-animated");
                    _item.style.setProperty("--pullup-delay", window.getComputedStyle(_item).animationDelay);

                    if (this.debug) return;

                    const _words = _splittext?.split?.words;

                    if (!_words) return;

                    let _timeOut =
                        (_words.length + 1) * parseFloat(window.getComputedStyle(_words[0]).animationDuration);
                    if (_timeOut <= 1) _timeOut = 1;

                    window.setTimeout(() => {
                        _splittext?.split?.revert();
                        _splittext.split = null;

                        _item.classList.remove("js-splittext-animate");
                        _item.style.setProperty("--pullup-delay", "");
                    }, _timeOut * 1000);
                });
            });
        }

        if (this.debug) console.log("_animate", this);
    }

    set(target: HTMLElement, delay: number, duration: number) {
        const _delay = parseFloat(delay) ? `${delay.toFixed(2)}s` : delay;

        target.style.animationDelay = _delay;
        if (parseFloat(delay)) target.style.setProperty("--animate-delay", _delay);
        target.style.setProperty("--animate-duration", `${duration}s`);
    }

    show(target: HTMLElement, callback?: (target: HTMLElement) => void) {
        this.staggerDelay += this.adjacentDelay;

        let _className = target.dataset.animateClass || target.dataset.animate?.split(",")[0] || this.keyframe;
        if (["parent", "children", "child"].includes(_className)) _className = this.keyframe;

        // show and animate the element
        target.classList.remove("opacity-0");
        target.classList.add("animate__animated", `animate__${_className}`);

        let _styles = window.getComputedStyle(target);
        // if no animation-duration
        if (parseFloat(_styles.animationDuration) === 0) target.style.animationDuration = `${this.duration}s`;
        // if no animation-delay
        if (parseFloat(_styles.animationDelay) === 0) {
            const _delay = `${this.adjacentDelay.toFixed(2)}s`;

            target.style.setProperty("--animate-delay", _delay);
            target.style.animationDelay = _delay;
        }

        // if should have stagger animation-delay
        if (target.dataset.animateDelay) {
            const _delay = parseFloat(this.staggerDelay) ? `${this.staggerDelay.toFixed(2)}s` : this.staggerDelay;

            target.style.setProperty("--animate-delay", _delay);
            target.style.animationDelay = _delay;
        }

        // for callback
        if (callback) {
            _styles = window.getComputedStyle(target);
            const _delay = parseFloat(_styles.animationDuration) + parseFloat(_styles.animationDelay);

            // console.log(
            //     "show",
            //     "_delay",
            //     parseFloat(_styles.animationDuration),
            //     parseFloat(_styles.animationDelay),
            //     _delay * 1000
            // );

            window.setTimeout(
                () => {
                    callback(target);
                },
                Math.max(_delay * 1000, 1400),
            );
        }
    }

    clean(target: HTMLElement, callback?: (target: HTMLElement) => void) {
        this.staggerDelay = 0;

        if (this.debug) return;

        let _className = target.dataset.animateClass || target.dataset.animate?.split(",")[0] || this.keyframe;
        if (["parent", "children", "child"].includes(_className)) _className = this.keyframe;

        // hide the element after delay
        const _styles = window.getComputedStyle(target);
        let _delay = parseFloat(_styles.animationDuration) + parseFloat(_styles.animationDelay);

        window.setTimeout(
            () => {
                target.removeAttribute("data-animate");
                target.removeAttribute("data-animate-delay");
                target.classList.remove("animate__animated", `animate__${_className}`, "opacity-0");

                target.style.animationDelay = "";
                target.style.setProperty("--animate-delay", "");
                target.style.animationDuration = "";
                target.style.setProperty("--animate-duration", "");
                target.style.animationFillMode = "";

                target.removeAttribute("data-animate-class");

                if (target.getAttribute("style")?.length <= 0) target.removeAttribute("style");

                if (callback) callback(target);
            },
            Math.max(_delay * 1000, 1400),
        );
    }

    style() {
        let _style = document.querySelector(".style-animate");
        if (!_style) {
            _style = document.createElement("style");
            document.head.appendChild(_style);

            _style.innerHTML =
                // `[data-animate] {` +
                // `&:not(.animate__animated) {` +
                // `opacity: 0;` +
                // `}` +
                // `}` +
                `.animate__animated {` +
                `-webkit-animation-duration: 1s;` +
                `animation-duration: 1s;` +
                `-webkit-animation-duration: var(--animate-duration);` +
                `animation-duration: var(--animate-duration);` +
                `-webkit-animation-fill-mode: both;` +
                `animation-fill-mode: both;` +
                `}` +
                `@keyframes fadeInUpSmall {` +
                `0% {` +
                `opacity: 0;` +
                `transform: translate3d(0, 40px, 0);` +
                `}` +
                `100% {` +
                `opacity: 1;` +
                `transform: translate3d(0, 0, 0);` +
                `}` +
                `}` +
                `.animate__fadeInUpSmall {` +
                `-webkit-animation-name: fadeInUpSmall;` +
                `animation-name: fadeInUpSmall;` +
                `}` +
                `@keyframes pullUp {` +
                `0% {` +
                `transform: translateY(calc(102% + 0.25em));` +
                `}` +
                `100% {` +
                `transform: translateY(0);` +
                `}` +
                `}` +
                // `.js-splittext .line-parent {` +
                // `position: relative;` +
                // `overflow: hidden;` +
                // `}` +
                `.js-splittext-animate .word {` +
                `-webkit-transform: translateY(100%);` +
                `-ms-transform: translateY(100%);` +
                `transform: translateY(100%);` +
                `}` +
                `.js-splittext-animated {` +
                `opacity: 1;` +
                `-webkit-animation: none;` +
                `animation: none;` +
                `-webkit-animation-delay: 0.1s;` +
                `animation-delay: 0.1s;` +
                `--pullup-delay: 0.1s;` +
                `}` +
                `.js-splittext-animated .word {` +
                `-webkit-animation-duration: 0.6s;` +
                `animation-duration: 0.6s;` +
                `-webkit-animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);` +
                `animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);` +
                `-webkit-animation-delay: calc(var(--pullup-delay) + 0.1s * var(--index));` +
                `animation-delay: calc(var(--pullup-delay) + 0.1s * var(--index));` +
                `-webkit-animation-fill-mode: forwards;` +
                `animation-fill-mode: forwards;` +
                `-webkit-animation-name: pullUp;` +
                `animation-name: pullUp;` +
                `}` +
                `.js-splittext-animated .word .emoji {` +
                `-webkit-animation-duration: 1s;` +
                `animation-duration: 1s;` +
                `-webkit-animation-name: popIn;` +
                `animation-name: popIn;` +
                `}`;
        }
    }
}
